import Template from "../app/Template";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import Button from "../layout/Button";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import Dropdown from "../forms/Dropdown";
import SectionHeader from "../layout/SectionHeader";
import { useEffect, useState } from "react";
import { getDirectoryEntries } from "../../slices/directory";
import { getCategoryOptions } from "../../slices/category";
import { Entry } from "../../types/directory";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import TextInputSubmitOnEnterLeadingIcon from "../forms/TextInputSubmitOnEnterLeadingIcon";
import Spinner from "../layout/Spinner";
import ExploreTopicsAndCollections from "../layout/ExploreTopicsAndCollections";
import { MapPinIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import FavouriteButton from "../layout/FavouriteButton";
import { withFilters } from "../../utils/searchParams";


export default function Directory() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams, setSearchParams] = useSearchParams();
  const querySearch = searchParams.get("query");
  const queryCategory = searchParams.get("category");
  const queryAudience = searchParams.get("audience");
  const queryPostcode = searchParams.get("postcode");
  const queryPageNumber = searchParams.get("page") === null
    ? 1
    : parseInt(searchParams.get("page") as string);

  useEffect(() => {
    dispatch(getDirectoryEntries({
      pageNumber: queryPageNumber,
      query: querySearch,
      category: queryCategory,
      audience: queryAudience,
      postcode: queryPostcode,
    }));
  }, [
    queryPageNumber,
    querySearch,
    queryCategory,
    queryAudience,
    queryPostcode
  ]);

  useEffect(() => {
    dispatch(getCategoryOptions())
  }, []);

  // Get app level state from redux store
  // const { user, loading: authLoading } = useAppSelector((state) => state.auth);
  // const {
  //   collections: { data: collectionData, loading: collectionLoading },
  // } = useAppSelector((state) => state.content);
  const {
    entries: { data: entriesData, loading: entriesLoading, waiting: entriesWaiting, pagination: entriesPagination },
  } = useAppSelector((state) => state.directory);
  const {
    options: { data: categoryOptionsData, loading: categoryOptionsLoading },
  } = useAppSelector((state) => state.category);

  // Component level state.
  //
  // Both boxes are seeded from the URL rather than starting empty, so a shared
  // /directory?query=...&postcode=... link renders with its search terms visible
  // in the fields instead of showing filtered results next to blank inputs.
  const [searchState, setSearchState] = useState(querySearch ?? "");
  const [postcodeState, setPostcodeState] = useState(queryPostcode ?? "");
  const [showPersonContactModal, setShowPersonContactModal] = useState(false);
  const [contactDetails, setContactDetails] = useState<{ phone: string; email: string; website: string | null }>({ phone: "", email: "", website: null });

  // Re-sync the boxes when the URL changes without a keystroke -- browser
  // back/forward, or a link into the page with filters already applied.
  useEffect(() => {
    setSearchState(querySearch ?? "");
  }, [querySearch]);
  useEffect(() => {
    setPostcodeState(queryPostcode ?? "");
  }, [queryPostcode]);

  const applyFilters = (changes: Record<string, string | null>) =>
    setSearchParams(withFilters(searchParams, changes));

  // Constant variables
  const dropdownSelect: Array<{ label: string; value: string }> = categoryOptionsData.directorycategory === undefined ? [] : categoryOptionsData.directorycategory.map((x: any) => ({ label: x.name, value: x.name }));
  const dropdownAudiences: Array<{ label: string; value: string }> = categoryOptionsData.audience === undefined ? [] : categoryOptionsData.audience.map((x: any) => ({ label: x.name, value: x.name }));

  return (
    <Template navbarMode="solid">

      {/* Show person contact details */}
      <Modal
        show={showPersonContactModal}
        setShow={setShowPersonContactModal}
        confirmText=""
        cancelText=""
        showActionButtons={false}
        showTriggerButton={false}
        confirmHandler={() => { }}
        bgColour="dark-beige"
      >
        <div className="overflow-auto px-1">
          <div className="text-center">
            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-black">
              Contact details
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              <TextInput
                id="phone-number"
                type="text"
                placeholder=""
                onChange={() => { }}
                required={false}
                showLabel={true}
                label="Phone number"
                widthClass="w-full"
                bgColour="off-white"
                textColour="black"
                placeholderColour="black"
                value={contactDetails.phone}
                disabled={true}
              />

              <TextInput
                id="email"
                type="email"
                placeholder=""
                onChange={() => { }}
                required={false}
                showLabel={true}
                label="Email"
                widthClass="w-full"
                bgColour="off-white"
                textColour="black"
                placeholderColour="black"
                value={contactDetails.email}
                disabled={true}
              />

              {contactDetails.website !== null && (
                <TextInput
                  id="website"
                  type="text"
                  placeholder=""
                  onChange={() => { }}
                  required={false}
                  showLabel={true}
                  label="Website"
                  widthClass="w-full"
                  bgColour="off-white"
                  textColour="black"
                  placeholderColour="black"
                  value={contactDetails.website}
                  disabled={true}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>

      <div className="grid grid-col-1 gap-y-20 mb-20">
        {/* Hero */}
        <div>
          <div
            className="mt-20 w-full bg-dark-blue py-12 md:py-20 relative"
          >
            <div className="grid grid-cols-1 gap-y-4 items-center justify-center">
              <h1 className="text-off-white text-3xl md:text-5xl lg:text-6xl font-bold mx-auto">Directory</h1>

              {/* Keyword search. The category dropdown that used to sit here now
                  lives in the "Refine search" rail with the other filters. */}
              <TextInputSubmitOnEnterLeadingIcon
                id="search-directory"
                placeholder="Search the directory"
                value={searchState}
                icon={
                  <MagnifyingGlassIcon
                    aria-hidden="true"
                    className="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-dark-grey sm:size-4"
                  />
                }
                widthClass="w-4/5 md:w-1/2 mx-auto"
                onChange={(e) => setSearchState(e.target.value)}
                onClick={() => {
                  // An empty box clears the keyword rather than doing nothing, so
                  // deleting the text and pressing Enter resets the list.
                  applyFilters({ query: searchState.trim() || null });
                }}
                loading={false}
                shadow="md"
              />
            </div>

            {/* Bubbles */}
            <img
              src="/img/graphics/bubbles/bubble-top-orange.svg"
              alt="Bubble"
              className="absolute w-20 md:w-36 object-cover -bottom-4 md:-bottom-8 right-16 z-0"
            />
          </div>
        </div>

        {/* Directory content */}
        <div className="flex justify-center w-full gap-8">
          <div className="grid grid-cols-1 md:grid-cols-10 gap-x-8 w-4/5">

            {/* Filters */}
            <div className="col-span-2 grid grid-cols-1 h-fit gap-4 align-start">
              <h2 className="font-bold text-dark-grey">Refine search</h2>

              {/* Category. Moved down out of the hero so every filter sits in one
                  place and the hero belongs to the keyword search. */}
              <Dropdown
                id="category-dropdown"
                className=""
                showLabel={false}
                required={false}
                onChange={(e) => applyFilters({ category: e.target.value })}
                value={queryCategory || ""}
              >
                <option key="" value="">
                  All Categories
                </option>
                {dropdownSelect.map((opt, idx) => (
                  <option key={`${idx}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Dropdown>

              {/* Audience */}
              <Dropdown
                id="audience-dropdown"
                className=""
                showLabel={false}
                required={false}
                onChange={(e) => applyFilters({ audience: e.target.value })}
                value={queryAudience || ""}
              >
                <option key="" value="">
                  All Audiences
                </option>
                {dropdownAudiences.map((opt, idx) => (
                  <option key={`${idx}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Dropdown>

              {/* Postcode. Replaces the city dropdown: entries are found by where
                  they actually are rather than by a city name picked off a list.
                  Matches from the start, so "M1" finds every M1 postcode and a
                  full "M1 4BT" finds the one. */}
              <div className="grid grid-cols-1 gap-1">
                <TextInputSubmitOnEnterLeadingIcon
                  id="postcode-directory"
                  placeholder="Postcode"
                  value={postcodeState}
                  icon={
                    <MapPinIcon
                      aria-hidden="true"
                      className="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-dark-grey sm:size-4"
                    />
                  }
                  borderClass="border border-dark-grey"
                  widthClass="w-full"
                  onChange={(e) => setPostcodeState(e.target.value)}
                  onClick={() => applyFilters({ postcode: postcodeState.trim() || null })}
                  loading={false}
                />
                <p className="text-xs text-dark-grey/70">
                  e.g. M1 or M1 4BT &mdash; press Enter to search
                </p>
              </div>

            </div>

            {/* Feed */}
            <div className="col-span-8 grid grid-cols-1">
              <>
                {entriesLoading ? <div className="w-full flex justify-center py-12"><Spinner size={8} /></div> : entriesData.map((entry: Entry, idx) => {
                  return (
                    <div
                      key={entry.id}
                      className="flex flex-nowrap w-full shrink-0 py-4 gap-4 content-card"
                      data-title={entry.name}
                      data-tags={entry.tags.map((tag: any) => tag.name).join(", ")}
                      onClick={() => {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                          event: "click",
                          item: "directory",
                          contentTitle: entry.name,
                          contentAuthor: "",
                          contentTags: entry.tags.map((tag: any) => tag.name).join(", "),
                        });
                      }}
                    >

                      {/* Left side: Fixed-width image */}
                      <div className="hidden sm:block shrink-0 w-[150px] md:w-[200px] h-[250px] overflow-hidden rounded-3xl pt-4">
                        <img
                          src={entry.images[0] || "/img/graphics/hero/landing-hero.png"}
                          alt="Speaker"
                          className="w-full h-full object-cover rounded-5xl"
                        />
                      </div>

                      {/* Right side: flexible width, relative for absolutely-positioned buttons */}
                      <div className={`relative h-[250px] overflow-y-auto scrollbar-invisible flex-1 flex-grow pt-4`}>
                        <div className={`h-full flex-1 flex-grow bg-dark-beige rounded-5xl ${entry.platform_approved && "border-2 border-dark-blue"} p-6 md:p-8`}>
                          <div className="mb-4 pr-20 md:pr-32">

                            {/* Sponsored badge */}
                            {entry.sponsored && (
                              <div
                                className={`
                                  absolute
                                  top-4
                                  left-28
                                  -translate-x-1/2
                                  -translate-y-1/2
                                  bg-dark-blue
                                  text-white
                                  px-4
                                  py-1
                                  rounded-full
                                  shadow-md
                                  text-sm md:text-base
                                  font-bold
                                `}
                              >
                                Sponsored
                              </div>
                            )}

                            {/* Header */}
                            <span className="flex gap-x-1 items-center w-full justify-start">
                              {/* <h2 title={entry.name} className="text-2xl font-bold text-dark-blue line-clamp-1 overflow-hidden text-ellipsis break-words"> */}
                              <h2 title={entry.name} className="min-w-0 truncate text-xl font-bold text-dark-blue">
                                {`${entry.name}`}
                              </h2>
                              {entry.platform_approved && (
                                <div className="relative inline-flex group">
                                  <CheckBadgeIcon className="w-5 h-5 text-orange shrink-0" />
                                  <span
                                    role="tooltip"
                                    className="
                                      absolute -top-8 left-1/2 -translate-x-1/2    /* sit centred above the icon */
                                      rounded-md bg-gray-900 text-white text-xs
                                      px-2 py-1 whitespace-nowrap
                                      opacity-0 group-hover:opacity-100 transition-opacity
                                      pointer-events-none
                                    "
                                  >
                                    Neurodivergent-led
                                  </span>
                                </div>
                              )}
                              {entry.founding_member && (
                                <div className="relative inline-flex group">
                                  <CheckBadgeIcon className="w-5 h-5 text-dark-blue shrink-0" />
                                  <span
                                    role="tooltip"
                                    className="
                                      absolute -top-8 left-1/2 -translate-x-1/2    /* sit centred above the icon */
                                      rounded-md bg-gray-900 text-white text-xs
                                      px-2 py-1 whitespace-nowrap
                                      opacity-0 group-hover:opacity-100 transition-opacity
                                      pointer-events-none
                                    "
                                  >
                                    Neuro-affirming
                                  </span>
                                </div>
                              )}
                            </span>

                            {/* Location */}
                            <span className="mt-1 flex items-center divide-x divide-dark-blue/50">
                              {entry.online && (
                                <span className="flex items-center gap-x-1 text-dark-blue text-sm pr-2">
                                  <VideoCameraIcon className="w-5 h-5 text-dark-blue shrink-0" />
                                  Online
                                </span>
                              )}
                              {/* City, with the postcode right of it. Rendered when
                                  the entry has either one, so an entry with a
                                  postcode but no city still shows where it is --
                                  and the pin never shows with nothing after it. */}
                              {((entry.city !== null && entry.city.name.length > 0) || entry.postcode) && (
                                <span className={`flex items-center gap-x-1 text-dark-blue text-sm ${entry.online && "pl-1"}`}>
                                  <MapPinIcon className="w-5 h-5 text-dark-blue shrink-0" />
                                  {entry.city !== null && entry.city.name}
                                  {entry.postcode && (
                                    <span className="font-semibold">{entry.postcode}</span>
                                  )}
                                </span>
                              )}
                            </span>

                            {/* Synopsis */}
                            <p className="mt-1 text-sm text-dark-blue line-clamp-4 overflow-hidden text-ellipsis break-words">{entry.synopsis}</p>

                            {/* Categories */}
                            <div className="mt-2 flex flex-wrap gap-2">
                              {entry.category.map((cat: any, index: number) => {
                                return (
                                  <span key={`tag-${index}`} className="flex items-center bg-transparent border border-dark-blue text-dark-blue px-3 py-0.2 rounded-full text-xs font-bold">{cat.name}</span>
                                );
                              })}
                            </div>
                          </div>

                          {/* Buttons pinned to the top-right, stacked vertically */}
                          <div className="absolute top-[45px] right-4 flex flex-col gap-2">

                            {/* Favourite */}
                            <FavouriteButton
                              className="w-fit mx-auto"
                              objectId={entry.id}
                              objectType="DirectoryEntry"
                              dataTitle={entry.name}
                              dataAuthor={entry.name}
                              dataTags={entry.tags.map((tag: any) => tag.name).join(", ")}
                            />

                            {/* Phone */}
                            <Button
                              bgColour="transparent"
                              textColour="dark-blue"
                              borderColour="dark-blue"
                              paddingY="1.5"
                              paddingX="3"
                              shadow="md"
                              onClick={() => {
                                setContactDetails({ phone: entry.main_contact.phone_number || "", email: entry.main_contact.email, website: entry.url })
                                setShowPersonContactModal(true)
                              }}
                            >
                              <span className="">Phone</span>
                            </Button>

                            {/* Email */}
                            <Button
                              bgColour="yellow"
                              textColour="dark-grey"
                              borderColour="yellow"
                              paddingY="1.5"
                              paddingX="3"
                              shadow="md"
                              onClick={() => {
                                setContactDetails({ phone: entry.main_contact.phone_number || "", email: entry.main_contact.email, website: entry.url })
                                setShowPersonContactModal(true)
                              }}
                            >
                              <span className="">Email</span>
                            </Button>

                            {/* Website */}
                            <Button
                              bgColour="dark-blue"
                              textColour="white"
                              borderColour="dark-blue"
                              paddingY="1.5"
                              paddingX="3"
                              shadow="md"
                              onClick={() => {
                                // setContactDetails({ phone: entry.main_contact.phone_number || "", email: entry.main_contact.email, website: entry.url })
                                // setShowPersonContactModal(true)
                                window.open(entry.url, "_blank");
                              }}
                            >
                              <span className="">Website</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
                {/* Pagination */}
                {entriesPagination.has_next && (
                  <div className="mt-8 flex items-center justify-center">
                    {entriesWaiting ? <Spinner size={6} /> : (
                      <Button
                        onClick={() => {
                          searchParams.set("page", (queryPageNumber + 1).toString());
                          setSearchParams(searchParams)
                        }}
                        width="fit"
                        bgColour="transparent hover:bg-dark-grey"
                        borderColour="dark-grey"
                        textColour="dark-grey hover:text-white"
                      >
                        Show more
                      </Button>
                    )}
                  </div>
                )}
              </>
            </div>
          </div>
        </div>

        {/* You may also like */}
        <SectionHeader
          text="You may also like"
        />

        {/* Topics and collections */}
        <ExploreTopicsAndCollections />

      </div>

    </Template>
  );
}


// import Template from "../app/Template";
// import { useAppDispatch, useAppSelector } from "../../hooks";
// import { useSearchParams } from "react-router-dom";
// import Button from "../layout/Button";
// import { CheckBadgeIcon } from "@heroicons/react/24/solid";
// import Dropdown from "../forms/Dropdown";
// import SectionHeader from "../layout/SectionHeader";
// import { useEffect, useState } from "react";
// import { getDirectoryEntries, getDirectoryEntryLocations } from "../../slices/directory";
// import { getCategoryOptions } from "../../slices/category";
// import { Entry } from "../../types/directory";
// import Modal from "../layout/Modal";
// import { Dialog } from "@headlessui/react";
// import TextInput from "../forms/TextInput";
// import Spinner from "../layout/Spinner";
// import ExploreTopicsAndCollections from "../layout/ExploreTopicsAndCollections";
// import { MapPinIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
// import FavouriteButton from "../layout/FavouriteButton";
// import FavouriteButtonFree from "../layout/FavouriteButtonFree";


// export default function Directory() {
//   const dispatch = useAppDispatch();

//   // Get query params
//   const [searchParams, setSearchParams] = useSearchParams();
//   const querySearch = searchParams.get("query");
//   const queryCategory = searchParams.get("category");
//   const queryAudience = searchParams.get("audience");
//   const queryLocation = searchParams.get("location");
//   const queryPageNumber = searchParams.get("page") === null
//     ? 1
//     : parseInt(searchParams.get("page") as string);

//   useEffect(() => {
//     dispatch(getDirectoryEntries({
//       pageNumber: queryPageNumber,
//       query: querySearch,
//       category: queryCategory,
//       audience: queryAudience,
//       location: queryLocation,
//     }));
//   }, [
//     queryPageNumber,
//     querySearch,
//     queryCategory,
//     queryAudience,
//     queryLocation
//   ]);

//   useEffect(() => {
//     dispatch(getCategoryOptions())
//     dispatch(getDirectoryEntryLocations())
//   }, []);

//   // Get app level state from redux store
//   // const { user, loading: authLoading } = useAppSelector((state) => state.auth);
//   // const {
//   //   collections: { data: collectionData, loading: collectionLoading },
//   // } = useAppSelector((state) => state.content);
//   const {
//     entries: { data: entriesData, loading: entriesLoading, waiting: entriesWaiting, pagination: entriesPagination },
//     options: { data: directoryOptionsData, loading: directoryOptionsLoading },
//   } = useAppSelector((state) => state.directory);
//   const {
//     options: { data: categoryOptionsData, loading: categoryOptionsLoading },
//   } = useAppSelector((state) => state.category);

//   // Component level state
//   // const [searchState, setSearchState] = useState("");
//   const [showPersonContactModal, setShowPersonContactModal] = useState(false);
//   const [contactDetails, setContactDetails] = useState<{ phone: string; email: string; website: string | null }>({ phone: "", email: "", website: null });

//   // Constant variables
//   const dropdownSelect: Array<{ label: string; value: string }> = categoryOptionsData.directorycategory === undefined ? [] : categoryOptionsData.directorycategory.map((x: any) => ({ label: x.name, value: x.name }));
//   const dropdownAudiences: Array<{ label: string; value: string }> = categoryOptionsData.audience === undefined ? [] : categoryOptionsData.audience.map((x: any) => ({ label: x.name, value: x.name }));
//   const dropdownLocations: Array<{ label: string; value: string }> = directoryOptionsData.cities === undefined ? [] : directoryOptionsData.cities.map((x: any) => ({ label: x.name, value: x.name }));

//   return (
//     <Template navbarMode="solid">

//       <div className="grid grid-col-1 gap-y-20 mb-20">
//         {/* Hero */}
//         <div>
//           <div
//             className="mt-20 w-full bg-dark-blue py-40 md:py-40 relative"
//           >
//             <div className="grid grid-cols-1 gap-y-4 items-center justify-center">
//               <h1 className="text-off-white text-3xl md:text-5xl lg:text-6xl font-bold mx-auto">Coming Soon!</h1>

//             </div>

//             {/* Bubbles */}
//             <img
//               src="/img/graphics/bubbles/bubble-top-orange.svg"
//               alt="Bubble"
//               className="absolute w-20 md:w-36 object-cover -bottom-4 md:-bottom-8 right-16 z-0"
//             />
//           </div>
//         </div>
//       </div>

//     </Template>
//   );
// }
